import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import debugLogger from '../lib/debugLogger'

/**
 * useDebug Hook - Component-level debugging utilities
 * 
 * Provides automatic lifecycle tracking, state change detection,
 * performance monitoring, and error catching for React components
 */

/**
 * Options for configuring the debug hook
 */
interface UseDebugOptions {
  enabled?: boolean
  logMounts?: boolean
  logUnmounts?: boolean
  logRenders?: boolean
  logStateChanges?: boolean
  trackPerformance?: boolean
  showVisualIndicators?: boolean
}

/**
 * Return type for useDebug hook
 */
interface UseDebugReturn {
  log: (message: string, data?: any) => void
  error: (message: string, data?: any) => void
  warn: (message: string, data?: any) => void
  info: (message: string, data?: any) => void
  timeStart: (operation: string) => void
  timeEnd: (operation: string) => void
  trackState: <T>(stateName: string, value: T) => void
  renderCount: number
  visualStyle: React.CSSProperties
}

/**
 * Default options for the hook
 */
const defaultOptions: UseDebugOptions = {
  enabled: true,
  logMounts: true,
  logUnmounts: true,
  logRenders: true,
  logStateChanges: true,
  trackPerformance: true,
  showVisualIndicators: false
}

/**
 * useDebug Hook
 * 
 * Comprehensive debugging hook for React components
 * Automatically tracks lifecycle events and provides debugging utilities
 * 
 * @param componentName - Name of the component for identification
 * @param options - Configuration options
 * @returns Object with debugging utilities
 */
export function useDebug(
  componentName: string,
  options: UseDebugOptions = {}
): UseDebugReturn {
  const opts = { ...defaultOptions, ...options }
  const renderCount = useRef(0)
  const mountedRef = useRef(false)
  const stateHistory = useRef<Map<string, any>>(new Map())
  const [, _forceUpdate] = useState({})

  /**
   * Track component mount
   * Logs when component first renders
   */
  useEffect(() => {
    if (opts.enabled && opts.logMounts) {
      debugLogger.lifecycle(componentName, 'mount')
      debugLogger.info(componentName, '🎬 Component mounted')
    }
    mountedRef.current = true

    // Track unmount
    return () => {
      if (opts.enabled && opts.logUnmounts) {
        debugLogger.lifecycle(componentName, 'unmount')
        debugLogger.info(componentName, '👋 Component unmounted', {
          totalRenders: renderCount.current
        })
      }
      mountedRef.current = false
    }
  }, [componentName, opts.enabled, opts.logMounts, opts.logUnmounts])

  /**
   * Track each render
   * Increments render count and logs render events
   */
  useEffect(() => {
    renderCount.current++
    
    if (opts.enabled && opts.logRenders) {
      debugLogger.lifecycle(componentName, 'render', {
        renderNumber: renderCount.current
      })
      
      // Warn if component re-renders excessively
      if (renderCount.current > 20 && renderCount.current % 10 === 0) {
        debugLogger.warn(
          componentName, 
          `⚠️ Excessive re-renders detected: ${renderCount.current} renders`,
          { renderCount: renderCount.current }
        )
      }
    }
  })

  /**
   * Log message with component context
   */
  const log = useCallback((message: string, data?: any) => {
    if (opts.enabled) {
      debugLogger.debug(componentName, message, data)
    }
  }, [componentName, opts.enabled])

  /**
   * Log error with component context
   */
  const error = useCallback((message: string, data?: any) => {
    if (opts.enabled) {
      debugLogger.error(componentName, message, data)
      debugLogger.lifecycle(componentName, 'error', { message, data })
    }
  }, [componentName, opts.enabled])

  /**
   * Log warning with component context
   */
  const warn = useCallback((message: string, data?: any) => {
    if (opts.enabled) {
      debugLogger.warn(componentName, message, data)
    }
  }, [componentName, opts.enabled])

  /**
   * Log info with component context
   */
  const info = useCallback((message: string, data?: any) => {
    if (opts.enabled) {
      debugLogger.info(componentName, message, data)
    }
  }, [componentName, opts.enabled])

  /**
   * Start performance timing for an operation
   */
  const timeStart = useCallback((operation: string) => {
    if (opts.enabled && opts.trackPerformance) {
      debugLogger.timeStart(componentName, operation)
    }
  }, [componentName, opts.enabled, opts.trackPerformance])

  /**
   * End performance timing for an operation
   */
  const timeEnd = useCallback((operation: string) => {
    if (opts.enabled && opts.trackPerformance) {
      const duration = debugLogger.timeEnd(componentName, operation)
      
      // Warn if operation takes too long
      if (duration && duration > 1000) {
        debugLogger.warn(
          componentName,
          `⚠️ Slow operation detected: ${operation} took ${duration.toFixed(2)}ms`
        )
      }
    }
  }, [componentName, opts.enabled, opts.trackPerformance])

  /**
   * Track state changes
   * Logs when state values change with previous/new values
   */
  const trackState = useCallback(<T,>(stateName: string, value: T) => {
    if (!opts.enabled || !opts.logStateChanges) return

    const previousValue = stateHistory.current.get(stateName)
    const hasChanged = previousValue !== value

    if (hasChanged) {
      debugLogger.debug(componentName, `📊 State change: ${stateName}`, {
        previous: previousValue,
        current: value,
        changed: hasChanged
      })
      
      stateHistory.current.set(stateName, value)
      debugLogger.lifecycle(componentName, 'update', {
        stateName,
        previousValue,
        currentValue: value
      })
    }
  }, [componentName, opts.enabled, opts.logStateChanges])

  /**
   * Visual debugging style
   * Adds colored borders to components for visual debugging
   */
  const visualStyle: React.CSSProperties = opts.showVisualIndicators ? {
    outline: '2px solid #00ff88',
    outlineOffset: '2px',
    position: 'relative'
  } : {}

  return {
    log,
    error,
    warn,
    info,
    timeStart,
    timeEnd,
    trackState,
    renderCount: renderCount.current,
    visualStyle
  }
}

/**
 * useDebugState Hook
 * Enhanced useState with automatic debug tracking
 * 
 * @param componentName - Name of the component
 * @param stateName - Name of the state variable
 * @param initialValue - Initial state value
 * @returns Tuple of [state, setState] with debug tracking
 */
export function useDebugState<T>(
  componentName: string,
  stateName: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(initialValue)
  const previousValue = useRef<T>(initialValue)

  useEffect(() => {
    if (previousValue.current !== state) {
      debugLogger.debug(componentName, `📊 ${stateName} changed`, {
        previous: previousValue.current,
        current: state
      })
      previousValue.current = state
    }
  }, [componentName, stateName, state])

  return [state, setState]
}

/**
 * useDebugEffect Hook
 * Enhanced useEffect with automatic logging
 * 
 * @param componentName - Name of the component
 * @param effectName - Name/description of the effect
 * @param effect - Effect function
 * @param deps - Dependency array
 */
export function useDebugEffect(
  componentName: string,
  effectName: string,
  effect: () => void | (() => void),
  deps?: React.DependencyList
): void {
  useEffect(() => {
    debugLogger.debug(componentName, `🔄 Effect running: ${effectName}`, {
      dependencies: deps
    })

    const cleanup = effect()

    return () => {
      if (cleanup) {
        debugLogger.debug(componentName, `🧹 Effect cleanup: ${effectName}`)
        cleanup()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * useDebugMemo Hook
 * Enhanced useMemo with performance tracking
 * 
 * @param componentName - Name of the component
 * @param memoName - Name/description of the memoized value
 * @param factory - Factory function to create memoized value
 * @param deps - Dependency array
 * @returns Memoized value
 */
export function useDebugMemo<T>(
  componentName: string,
  memoName: string,
  factory: () => T,
  deps: React.DependencyList
): T {
  const startTime = useRef<number>(0)

  useEffect(() => {
    debugLogger.debug(componentName, `🔄 Memo recalculating: ${memoName}`, {
      dependencies: deps
    })
  }, deps)

  return useMemo(() => {
    startTime.current = performance.now()
    const result = factory()
    const duration = performance.now() - startTime.current

    debugLogger.debug(componentName, `✅ Memo calculated: ${memoName}`, {
      duration: `${duration.toFixed(2)}ms`
    })

    // Warn if calculation takes too long
    if (duration > 100) {
      debugLogger.warn(
        componentName,
        `⚠️ Slow memo calculation: ${memoName} took ${duration.toFixed(2)}ms`
      )
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * useDebugCallback Hook
 * Enhanced useCallback with tracking
 * 
 * @param componentName - Name of the component
 * @param callbackName - Name/description of the callback
 * @param callback - Callback function
 * @param deps - Dependency array
 * @returns Memoized callback
 */
export function useDebugCallback<T extends (...args: any[]) => any>(
  componentName: string,
  callbackName: string,
  callback: T,
  deps: React.DependencyList
): T {
  useEffect(() => {
    debugLogger.debug(componentName, `🔄 Callback recreated: ${callbackName}`, {
      dependencies: deps
    })
  }, deps)

  return useCallback((...args: any[]) => {
    debugLogger.trace(componentName, `📞 Callback called: ${callbackName}`, {
      arguments: args
    })
    return callback(...args)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps) as T
}

/**
 * withDebug HOC
 * Higher-order component to add debugging to any component
 * 
 * @param Component - Component to wrap
 * @param componentName - Name for debugging
 * @param options - Debug options
 * @returns Wrapped component with debugging
 */
export function withDebug<P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string,
  options?: UseDebugOptions
): React.ComponentType<P> {
  const DebugWrappedComponent: React.FC<P> = (props: P) => {
    const name = componentName || Component.displayName || Component.name || 'Unknown'
    const debug = useDebug(name, options)

    useEffect(() => {
      debug.log('Props updated', props)
    }, [props, debug])

    try {
      return React.createElement(Component, props)
    } catch (err) {
      debug.error('Render error', err)
      throw err
    }
  }

  DebugWrappedComponent.displayName = `withDebug(${componentName || Component.displayName || Component.name})`
  
  return DebugWrappedComponent
}

export default useDebug